const HIGHER_IS_BETTER = [
  "storage",
  "ram",
  "memory",
  "battery_life",
  "battery",
  "resolution",
  "rating",
  "camera",
  "megapixel",
  "megapixels",
  "mp",
  "refresh_rate",
  "refresh",
  "cpu",
  "processor",
  "clock_speed",
  "os_update",
  "os_updates",
  "software_update",
  "update_support",
  "brightness",
  "capacity",
  "power_output",
  "zoom",
  "cores",
  "threads",
  "cache",
  "sample_rate",
  "bit_depth",
  "hdmi_ports",
  "buttons",
  "sip_accounts",
  "screen",
  "display",
  "horsepower",
  "torque",
  "range",
  "mileage",
  "bandwidth",
  "charging",
  "watts",
  "watt",
  "resistance",
  "waterproof",
];

const LOWER_IS_BETTER = [
  "price",
  "weight",
  "latency",
  "response_time",
  "input_lag",
  "thickness",
];

const matchesFieldKey = (fieldName, key) => {
  if (fieldName === key) return true;
  if (fieldName.startsWith(`${key}_`) || fieldName.endsWith(`_${key}`)) return true;
  return fieldName.split(/[_\s-]+/).includes(key);
};

export const parseBooleanValue = (value) => {
  if (value === true || value === false) return value;
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) return true;
  if (["false", "no", "0"].includes(normalized)) return false;
  return null;
};

export const parseNumericValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  if (typeof rawValue === "number" && Number.isFinite(rawValue)) return rawValue;

  const str = String(rawValue).replace(/,/g, "").trim().toLowerCase();
  const numbers = [...str.matchAll(/(\d+(?:\.\d+)?)/g)].map((match) =>
    parseFloat(match[1]),
  );
  if (!numbers.length) return null;

  // Range-style values (e.g. "6.5-7 in") — use the upper bound for ranking.
  let num = numbers.length > 1 && str.includes("-") ? Math.max(...numbers) : numbers[0];
  if (!Number.isFinite(num)) return null;

  if (/tb/i.test(str)) num *= 1024;
  if (/ghz/i.test(str)) num *= 1000;

  return num;
};

const isWaterRatingField = (field) => {
  const name = (field?.name || "").toLowerCase();
  return (
    matchesFieldKey(name, "resistance") || matchesFieldKey(name, "waterproof")
  );
};

export const parseIpRating = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  const str = String(rawValue).trim().toLowerCase();
  if (["none", "no", "n/a"].includes(str)) return 0;

  const ipMatch = str.match(/ip(\d)(\d)/i);
  if (ipMatch) return parseInt(`${ipMatch[1]}${ipMatch[2]}`, 10);

  const ipxMatch = str.match(/ipx(\d)/i);
  if (ipxMatch) return 50 + parseInt(ipxMatch[1], 10);

  return null;
};

export const parseComparableNumericValue = (rawValue, field) => {
  if (isWaterRatingField(field)) return parseIpRating(rawValue);
  return parseNumericValue(rawValue);
};

const resolveCompareDirection = (field, entries) => {
  const direction = getCompareDirection(field);
  if (direction) return direction;

  const boolValues = entries
    .map((entry) => parseBooleanValue(entry.raw))
    .filter((value) => value !== null);
  if (boolValues.length < 2) return null;
  if (boolValues.every((value) => value === boolValues[0])) return null;
  return "boolean";
};

/** Returns "higher" | "lower" | "boolean" | null — null means do not rank/highlight */
export const getCompareDirection = (field) => {
  const name = (field?.name || "").toLowerCase();
  const type = field?.type;

  if (type === "checkbox") return "boolean";
  if (type === "color") return null;

  if (HIGHER_IS_BETTER.some((key) => matchesFieldKey(name, key))) return "higher";
  if (LOWER_IS_BETTER.some((key) => matchesFieldKey(name, key))) return "lower";

  return null;
};

export const formatCompareFieldValue = (rawValue, field) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return "—";

  const boolValue = parseBooleanValue(rawValue);
  if (boolValue === true) return "Yes";
  if (boolValue === false) return "No";

  if (field?.type === "number" || field?.type === "range" || field?.type === "weight") {
    const numeric = parseNumericValue(rawValue);
    if (numeric !== null) {
      return field?.unit ? `${numeric} ${field.unit}` : String(numeric);
    }
  }

  return String(rawValue);
};

export const computeFieldComparisons = (products, fields, getRawFieldValue) => {
  if (products.length < 2 || !fields.length) return {};

  const comparison = {};

  fields.forEach((field) => {
    const entries = products.map((product) => ({
      product,
      raw: getRawFieldValue(product, field.name),
    }));

    const direction = resolveCompareDirection(field, entries);

    const displayValues = entries.map((entry) =>
      formatCompareFieldValue(entry.raw, field),
    );
    const allSame = displayValues.every((value) => value === displayValues[0]);

    if (!direction) {
      comparison[field.name] = { allSame, type: "neutral" };
      return;
    }

    if (direction === "boolean") {
      const boolEntries = entries.map((entry) => ({
        ...entry,
        bool: parseBooleanValue(entry.raw),
      }));
      const valid = boolEntries.filter((entry) => entry.bool !== null);

      if (valid.length === 0 || valid.every((entry) => entry.bool === valid[0].bool)) {
        comparison[field.name] = { allSame: true, type: "boolean" };
        return;
      }

      comparison[field.name] = {
        allSame: false,
        type: "boolean",
        best: valid.filter((entry) => entry.bool).map((entry) => entry.product._id),
        worst: valid
          .filter((entry) => entry.bool === false)
          .map((entry) => entry.product._id),
      };
      return;
    }

    const numericEntries = entries.map((entry) => ({
      ...entry,
      numeric: parseComparableNumericValue(entry.raw, field),
    }));
    const valid = numericEntries.filter((entry) => entry.numeric !== null);

    if (valid.length < 2) {
      comparison[field.name] = { allSame, type: "numeric" };
      return;
    }

    const sorted = [...valid].sort((a, b) =>
      direction === "higher" ? b.numeric - a.numeric : a.numeric - b.numeric,
    );
    const bestValue = sorted[0].numeric;
    const worstValue = sorted[sorted.length - 1].numeric;

    if (bestValue === worstValue) {
      comparison[field.name] = { allSame: true, type: "numeric" };
      return;
    }

    comparison[field.name] = {
      allSame: false,
      type: "numeric",
      best: valid
        .filter((entry) => entry.numeric === bestValue)
        .map((entry) => entry.product._id),
      worst: valid
        .filter((entry) => entry.numeric === worstValue)
        .map((entry) => entry.product._id),
    };
  });

  return comparison;
};

export const isComparisonBest = (comparison, productId) =>
  Array.isArray(comparison?.best) && comparison.best.includes(productId);

export const isComparisonWorst = (comparison, productId) =>
  Array.isArray(comparison?.worst) && comparison.worst.includes(productId);
